import axios, { type AxiosResponse } from 'axios';
import { getConfig } from '../config';
import type { FcDashboardToday, FcReportSnapshot } from '@/models/FcDashboard';

/** The counts are a handful of reads on the server; anything slower than this is a server in trouble, not a slow page. */
const REQUEST_TIMEOUT_MS = 30_000;

/** Reads the FC Dashboard (SSD-7602) and the Fulfillment Report copy (SSD-7638). Read-only: the dashboard never writes. */
export class FcDashboardClient {
  /**
   * Reads still on their way, by address and day. On dev the page asked twice within milliseconds as it opened (the
   * cause sits outside this screen; the Manual Carrier Cancels screen shows the same pair), and two screens may ask at
   * once; a second ask for the same day shares the first read rather than running the server's counts again.
   */
  private static readonly inFlight = new Map<string, Promise<FcDashboardToday>>();

  /** Report reads still on their way, by address: the tab opens with the same double ask as Today, so the second shares the first. */
  private static readonly reportInFlight = new Map<string, Promise<FcReportSnapshot | null>>();

  constructor(private readonly apiUrl: string) {}

  private get headers(): Record<string, string> {
    return { Accept: 'application/json', 'x-api-key': getConfig().apiKey };
  }

  /** The Today tab: `date` is the KST day the day cards count; the server takes yesterday when it is absent. */
  public getToday(date?: string): Promise<FcDashboardToday> {
    const key = `${this.apiUrl}|${date ?? ''}`;
    const pending = FcDashboardClient.inFlight.get(key);
    if (pending) {
      return pending;
    }
    const read = this.read(date).finally(() => FcDashboardClient.inFlight.delete(key));
    FcDashboardClient.inFlight.set(key, read);
    return read;
  }

  /** The latest Fulfillment Report copy, or null before the first copy has arrived (the server answers 404 then). */
  public getReportSnapshot(): Promise<FcReportSnapshot | null> {
    const pending = FcDashboardClient.reportInFlight.get(this.apiUrl);
    if (pending) {
      return pending;
    }
    const read = this.readReportSnapshot().finally(() => FcDashboardClient.reportInFlight.delete(this.apiUrl));
    FcDashboardClient.reportInFlight.set(this.apiUrl, read);
    return read;
  }

  private async readReportSnapshot(): Promise<FcReportSnapshot | null> {
    const response: AxiosResponse<FcReportSnapshot> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/fulfillmentReport/snapshot/latest`,
      {
        headers: this.headers,
        timeout: REQUEST_TIMEOUT_MS,
        validateStatus: (status) => status === 200 || status === 404,
      },
    );
    return response.status === 404 ? null : response.data;
  }

  private async read(date?: string): Promise<FcDashboardToday> {
    const response: AxiosResponse<FcDashboardToday> = await axios.get(
      `${this.apiUrl}/fulfillment/admin/dashboard/today`,
      {
        headers: this.headers,
        params: date ? { date } : undefined,
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return response.data;
  }
}
